import { useMemo, useState } from 'react';
import { Card } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useClientsAndInvitations } from '../../hooks/useClientsAndInvitations';

type ClientPickerProps = {
  onConfirm: (selection: { clientUserId: string | null; name: string | null }) => void;
  onCancel: () => void;
};

const MAX_NAME_LENGTH = 200;

export const ClientPicker = ({ onConfirm, onCancel }: ClientPickerProps) => {
  const { data: clientsAndInvites = [], isLoading } = useClientsAndInvitations();
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [planName, setPlanName] = useState('');

  const connectedClients = useMemo(
    () => clientsAndInvites.filter((entry) => entry.type === 'CONNECTED'),
    [clientsAndInvites],
  );

  const selectedClientLabel = useMemo(() => {
    if (selectedClientId === null) return null;
    const match = connectedClients.find((c) => c.type === 'CONNECTED' && c.id === selectedClientId);
    if (!match || match.type !== 'CONNECTED') return null;
    return [match.firstName, match.lastName].filter(Boolean).join(' ').trim() || match.email;
  }, [connectedClients, selectedClientId]);

  const namePlaceholder = selectedClientLabel
    ? `${selectedClientLabel}'s care guide`
    : "e.g. Client name's care guide";

  const handleConfirm = () => {
    onConfirm({
      clientUserId: selectedClientId,
      name: planName.trim() ? planName.trim().slice(0, MAX_NAME_LENGTH) : null,
    });
  };

  return (
    <Card className="p-6 space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-termina text-xl font-medium text-text-primary">Start a new Care Guide</h2>
          <p className="text-sm text-gray-600 mt-1">
            Optionally pick a client to attach this Q&A to. Sharing and referrals from the results
            page will pre-fill that client. You can skip and pick a client later.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            className="bg-[#026857] hover:bg-[#025247]"
          >
            Continue
          </Button>
        </div>
      </div>

      <fieldset className="space-y-2">
        <legend className="sr-only">Choose a client</legend>

        <label className="flex items-start gap-3 rounded-md border border-gray-200 p-3 cursor-pointer hover:border-[#026857]">
          <input
            type="radio"
            name="fp-qa-client"
            value=""
            checked={selectedClientId === null}
            onChange={() => setSelectedClientId(null)}
            className="mt-1"
          />
          <div>
            <p className="text-sm font-medium text-gray-900">No client (client-agnostic Q&A)</p>
            <p className="text-xs text-gray-600 mt-0.5">
              Pick a client at the time you Refer or Share from the results.
            </p>
          </div>
        </label>

        {isLoading ? (
          <Skeleton className="h-12 w-full" />
        ) : connectedClients.length === 0 ? (
          <p className="text-sm text-gray-500 px-3">
            You don&apos;t have any connected clients yet. The Q&A will run client-agnostic.
          </p>
        ) : (
          connectedClients.map((entry) => {
            if (entry.type !== 'CONNECTED') return null;
            const fullName = [entry.firstName, entry.lastName].filter(Boolean).join(' ').trim();
            return (
              <label
                key={entry.id}
                className="flex items-start gap-3 rounded-md border border-gray-200 p-3 cursor-pointer hover:border-[#026857]"
              >
                <input
                  type="radio"
                  name="fp-qa-client"
                  value={entry.id}
                  checked={selectedClientId === entry.id}
                  onChange={() => setSelectedClientId(entry.id)}
                  className="mt-1"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">{fullName || entry.email}</p>
                  <p className="text-xs text-gray-600 mt-0.5">{entry.email}</p>
                </div>
              </label>
            );
          })
        )}
      </fieldset>

      <div className="space-y-1.5">
        <label htmlFor="fp-qa-plan-name" className="text-sm font-medium text-gray-900">
          Plan name <span className="text-gray-500 font-normal">(optional)</span>
        </label>
        <input
          id="fp-qa-plan-name"
          type="text"
          value={planName}
          maxLength={MAX_NAME_LENGTH}
          onChange={(e) => setPlanName(e.target.value)}
          placeholder={namePlaceholder}
          className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[#026857] focus:outline-none focus:ring-1 focus:ring-[#026857]"
        />
        <p className="text-xs text-gray-500">
          You can rename the plan later from the results page. If you leave this blank, we’ll show
          the care stage as the title.
        </p>
      </div>

      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          className="bg-[#026857] hover:bg-[#025247]"
        >
          Continue
        </Button>
      </div>
    </Card>
  );
};
